import * as THREE from "three";
import { assetUrl } from "../assetVer.js";
import { timeOfDayAt } from "@ghostland/shared";

/** Nearest-filter loader for the pixel-art sky assets (sun/moon/clouds), version-busted for CDN. */
function pixTex(file: string): THREE.Texture {
  const t = new THREE.TextureLoader().load(assetUrl(`/assets/scene/kapon/${file}`));
  t.magFilter = THREE.NearestFilter; t.minFilter = THREE.NearestFilter; t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/**
 * Procedural day/night sky + sun/moon/stars + time-driven lighting. A single shared controller
 * (like distanceBlur): the scene registers its sky dome + lights here; world3d advances `time`
 * (24h clock) each frame and the debug panel scrubs it. setTime() repaints a horizon→zenith gradient
 * dome, arcs the sun + moon, fades the stars in at night, and tints the key/fill/hemi/ambient lights
 * + the exposed `horizon` colour (world3d copies it into the fog + background while outdoors).
 *
 * No GLSL — the sky is a CanvasTexture gradient + glow sprites, so it always renders + stays on the
 * pixel-art aesthetic once the world is downsampled.
 */

interface Phase {
  h: number;            // hour (0..24)
  zenith: number; horizon: number; // sky gradient
  light: number; lightI: number;   // key (sun/moon) directional colour + intensity
  hemiSky: number; hemiGround: number; hemiI: number;
  ambient: number; ambientI: number;
  tint: number;         // multiplier applied to UNLIT sprite materials (trees/items/chars) so they
                        // read as day/night too (white = full daylight, dark blue = night)
}

// Key colours through the day; setTime lerps between the two bracketing phases (wraps 21→24/0).
const PHASES: Phase[] = [
  { h: 0,    zenith: 0x070b1a, horizon: 0x121a30, light: 0x8fa0d0, lightI: 0.15, hemiSky: 0x2a3358, hemiGround: 0x141a26, hemiI: 0.35, ambient: 0x20283f, ambientI: 0.22, tint: 0x2b3760 },
  { h: 5,    zenith: 0x16213f, horizon: 0x4a3f60, light: 0xb9a0c0, lightI: 0.40, hemiSky: 0x3a3a5a, hemiGround: 0x24222e, hemiI: 0.50, ambient: 0x2e2c40, ambientI: 0.26, tint: 0x4e4a6e },
  { h: 6.5,  zenith: 0x355a92, horizon: 0xff9a5a, light: 0xffc890, lightI: 1.00, hemiSky: 0x88a0c8, hemiGround: 0x5a5040, hemiI: 0.80, ambient: 0x8a7a64, ambientI: 0.30, tint: 0xffd2ac },
  { h: 9,    zenith: 0x3f7fc8, horizon: 0xbfe0f2, light: 0xfff0d4, lightI: 1.50, hemiSky: 0xbfd8f0, hemiGround: 0x5c7a44, hemiI: 1.00, ambient: 0x9a8a6a, ambientI: 0.32, tint: 0xfff2e2 },
  { h: 12,   zenith: 0x3a82d8, horizon: 0xd6ecff, light: 0xfffaf0, lightI: 1.80, hemiSky: 0xcfe4ff, hemiGround: 0x5c7a44, hemiI: 1.05, ambient: 0x9a8a6a, ambientI: 0.32, tint: 0xffffff },
  { h: 15,   zenith: 0x3f86cc, horizon: 0xcfe6f8, light: 0xfff2d8, lightI: 1.60, hemiSky: 0xc4dcf2, hemiGround: 0x5c7a44, hemiI: 1.00, ambient: 0x9a8a6a, ambientI: 0.32, tint: 0xfff4e6 },
  { h: 17.5, zenith: 0x395a92, horizon: 0xff7a46, light: 0xffb070, lightI: 1.00, hemiSky: 0x90a0c0, hemiGround: 0x5a4838, hemiI: 0.80, ambient: 0x8a6e58, ambientI: 0.30, tint: 0xffc59a },
  { h: 19,   zenith: 0x1c2a4a, horizon: 0x6a3a56, light: 0xc88aa0, lightI: 0.40, hemiSky: 0x3a3a5a, hemiGround: 0x241e28, hemiI: 0.50, ambient: 0x2e2a40, ambientI: 0.26, tint: 0x6e5a7c },
  { h: 21,   zenith: 0x0b1124, horizon: 0x16203a, light: 0x8fa0d0, lightI: 0.18, hemiSky: 0x2a3358, hemiGround: 0x141a26, hemiI: 0.38, ambient: 0x222a42, ambientI: 0.24, tint: 0x303c64 },
];

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/** Radial glow sprite texture (white core → transparent), reused for sun + moon (tinted per-frame). */
function glowTexture(): THREE.CanvasTexture {
  const S = 64, c = document.createElement("canvas"); c.width = c.height = S;
  const ctx = c.getContext("2d")!;
  const g = ctx.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
  g.addColorStop(0, "rgba(255,255,255,1)");
  g.addColorStop(0.18, "rgba(255,255,255,0.95)");
  g.addColorStop(0.45, "rgba(255,255,255,0.35)");
  g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g; ctx.fillRect(0, 0, S, S);
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t;
}

interface Cloud { sp: THREE.Sprite; az: number; el: number; speed: number; base: number; aspect: number }

class DayNight {
  /** Current time on a 24h clock. */
  time = 9;
  /** Auto-advance speed in game-hours per real second (0 = frozen) — used only in FREE (debug) mode. */
  autoSpeed = 0.05;
  /** When synced to the server clock, time is derived from the wall clock (shared `timeOfDayAt`) so all
   *  clients + relogins agree. The debug panel switches to FREE mode (scrub/setDaySpeed). */
  private synced = false;
  private clockOffsetMs = 0; // serverEpoch - clientEpoch at sync (corrects client clock skew)
  /** Live horizon colour for the current time — world3d copies it into the outdoor fog + background. */
  readonly horizon = new THREE.Color(0xbfe0f2);
  /** Live daylight multiplier for UNLIT sprite materials (trees/dropped items/players/NPCs/mobs) so
   *  they darken + cool at night like the lit ground does. White (no tint) while the cycle is off. */
  readonly tint = new THREE.Color(0xffffff);
  /** Only tint/colour while we're in the outdoor (day/night) zone; white + neutral otherwise. */
  private enabled = false;
  /** Unlit sprite materials tinted by the cycle → their (optional) world position, so nearby lamps can
   *  brighten them at night (position-aware via lightAt). null pos = plain tint (no lamp lighting). */
  private readonly tintMats = new Map<THREE.Material & { color: THREE.Color }, THREE.Vector3 | null>();
  /** Point-light sources (lamps, neon signs) the unlit sprites pick up at night. Scaled colour added. */
  private readonly lights: { x: number; z: number; r: number; cr: number; cg: number; cb: number; i: number }[] = [];
  /** 0 (full day) → 1 (deep night); lamps only contribute as it gets dark so daytime stays clean. */
  private night = 0;

  private readonly radius = 300;
  private skyGroup: THREE.Group | null = null;     // holds dome+sun+moon+stars+clouds; follows the player
  private skyCanvas: HTMLCanvasElement | null = null;
  private skyTex: THREE.CanvasTexture | null = null;
  private sun: THREE.Sprite | null = null;
  private moon: THREE.Sprite | null = null;
  private stars: THREE.Points | null = null;
  private clouds: Cloud[] = [];
  private key: THREE.DirectionalLight | null = null;
  private fill: THREE.DirectionalLight | null = null;
  private hemi: THREE.HemisphereLight | null = null;
  private ambient: THREE.AmbientLight | null = null;
  private lastGradH = -99;
  // Throttle + change-detection for the O(all-sprites-in-world) re-tint. The tint moves over MINUTES
  // (a full day is 16 min), so recomputing it every frame is pure waste — and the per-sprite loop ran
  // even indoors. We recompute a few times a second and only walk the sprite set when the colour moved.
  private tickAccum = 0;
  private lastTintHex = -1;
  private lastTintNight = -1;

  /** Build ONE player-following sky rig (dome + sun + moon + stars + clouds, in a group centred on the
   *  origin) + global day/night lights, all added to the scene. The rig + lights are shown ONLY in
   *  outdoor zones (setEnabled) and follow the player (setCenter). Call once at startup. */
  build(scene: THREE.Scene): void {
    const radius = this.radius;
    const g = new THREE.Group(); this.skyGroup = g; scene.add(g);

    const cv = document.createElement("canvas"); cv.width = 4; cv.height = 256;
    this.skyCanvas = cv;
    const tex = new THREE.CanvasTexture(cv);
    tex.colorSpace = THREE.SRGBColorSpace; tex.magFilter = THREE.LinearFilter; tex.minFilter = THREE.LinearFilter;
    this.skyTex = tex;
    const sky = new THREE.Mesh(
      new THREE.SphereGeometry(radius, 40, 24),
      new THREE.MeshBasicMaterial({ map: tex, side: THREE.BackSide, fog: false, depthWrite: false }),
    );
    sky.renderOrder = -10; g.add(sky); // local origin (the group follows the player)

    // Pixel-art sun + moon (glow baked in → normal alpha blending stays crisp). depthTest ON so the
    // gate/walls/trees/terrain occlude them at the horizon (depthWrite off so they blend with the dome).
    const sun = new THREE.Sprite(new THREE.SpriteMaterial({ map: pixTex("sun.png"), transparent: true, depthWrite: false, depthTest: true, fog: false }));
    sun.scale.setScalar(radius * 0.17); sun.renderOrder = -9; g.add(sun); this.sun = sun;
    const moon = new THREE.Sprite(new THREE.SpriteMaterial({ map: pixTex("moon.png"), transparent: true, depthWrite: false, depthTest: true, fog: false }));
    moon.scale.setScalar(radius * 0.12); moon.renderOrder = -9; g.add(moon); this.moon = moon;

    // Stars: a points field on the upper dome (local coords), faded in at night.
    const N = 320, pos = new Float32Array(N * 3);
    for (let i = 0; i < N; i++) {
      const u = Math.random(), v = Math.random();
      const theta = u * Math.PI * 2, phi = Math.acos(0.15 + v * 0.85); // y >= ~0.15
      pos[i * 3] = Math.sin(phi) * Math.cos(theta) * radius * 0.97;
      pos[i * 3 + 1] = Math.cos(phi) * radius * 0.97;
      pos[i * 3 + 2] = Math.sin(phi) * Math.sin(theta) * radius * 0.97;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    const stars = new THREE.Points(geo, new THREE.PointsMaterial({
      map: glowTexture(), size: radius * 0.012, transparent: true, depthWrite: false, depthTest: true,
      fog: false, color: 0xffffff, opacity: 0, blending: THREE.AdditiveBlending, sizeAttenuation: true,
    }));
    stars.renderOrder = -8; g.add(stars); this.stars = stars;

    // Drifting daytime clouds — pixel-art sprites, random variant/position/size/speed; faded in by day.
    const cloudTexs = [pixTex("cloud1.png"), pixTex("cloud2.png"), pixTex("cloud3.png"), pixTex("cloud4.png"), pixTex("cloud5.png")];
    const cloudAspect = [72 / 46, 56 / 42, 92 / 48, 64 / 44, 100 / 46];
    this.clouds = [];
    const CLOUDS = 12;
    for (let i = 0; i < CLOUDS; i++) {
      const v = Math.floor(Math.random() * cloudTexs.length);
      const m = new THREE.SpriteMaterial({ map: cloudTexs[v], transparent: true, depthWrite: false, depthTest: true, fog: false, opacity: 0 });
      const sp = new THREE.Sprite(m);
      const sc = radius * (0.055 + Math.random() * 0.07);
      sp.scale.set(sc * cloudAspect[v], sc, 1);
      sp.renderOrder = -8.5; sp.frustumCulled = false; g.add(sp);
      const az = (i / CLOUDS) * Math.PI * 2 + Math.random() * 0.5;
      const cloud: Cloud = { sp, az, el: 0.09 + Math.random() * 0.30, speed: 0.004 + Math.random() * 0.012, base: 0.7 + Math.random() * 0.3, aspect: cloudAspect[v] };
      this.placeCloud(cloud); this.clouds.push(cloud);
    }

    // Global day/night lights (added to the scene; shown only outdoors via setEnabled). setTime drives
    // their colour/intensity/direction. Directional lights are parallel → one set lights every outdoor
    // zone regardless of where it sits in the world.
    this.key = new THREE.DirectionalLight(0xffffff, 1); scene.add(this.key);
    this.fill = new THREE.DirectionalLight(0xffffff, 0.3); scene.add(this.fill);
    this.hemi = new THREE.HemisphereLight(0xffffff, 0x444444, 1); scene.add(this.hemi);
    this.ambient = new THREE.AmbientLight(0xffffff, 0.3); scene.add(this.ambient);

    g.visible = false;
    this.key.visible = this.fill.visible = this.hemi.visible = this.ambient.visible = false;
    this.setTime(this.time);
  }

  /** Move the sky rig to follow the player (world units) so the sky always encloses the view. */
  setCenter(x: number, z: number): void { this.skyGroup?.position.set(x, 0, z); }

  /** Position a cloud on the dome from its azimuth + elevation (local to the player-following group). */
  private placeCloud(c: Cloud): void {
    const ce = Math.cos(c.el), R = this.radius * 0.9;
    c.sp.position.set(ce * Math.cos(c.az) * R, Math.sin(c.el) * R, ce * Math.sin(c.az) * R);
  }

  /** Register an UNLIT sprite material to be tinted by the day/night `tint` (its base map should be
   *  white — the tint multiplies it). Pass the sprite's world `pos` so nearby lamps light it at night. */
  registerMat(mat: (THREE.Material & { color?: THREE.Color }) | null | undefined, pos?: THREE.Vector3): void {
    if (!mat || !(mat as { color?: THREE.Color }).color) return;
    const m = mat as THREE.Material & { color: THREE.Color };
    this.tintMats.set(m, pos ?? null);
    // Apply the current tint right away — the per-frame loop is throttled/skipped when unchanged, so a
    // sprite added between re-tints would otherwise stay un-tinted (full-white) until the next change.
    if (pos) this.lightAt(pos.x, pos.z, m.color); else m.color.copy(this.tint);
  }

  /** Drop a material from the tint set (e.g. a dropped item that was picked up / despawned). */
  unregisterMat(mat: THREE.Material | null | undefined): void {
    if (mat) this.tintMats.delete(mat as THREE.Material & { color: THREE.Color });
  }

  /** Re-scan the scene for PointLights (lamps, neon sign glows) so unlit sprites can be lit by them at
   *  night. Call after building/expanding the world (boot + lazy city build). Clears + rebuilds. */
  collectLights(scene: THREE.Object3D): void {
    this.lights.length = 0;
    const wp = new THREE.Vector3();
    scene.traverse((o) => {
      const L = o as THREE.PointLight;
      if (!L.isPointLight || L.intensity <= 0) return;
      L.getWorldPosition(wp);
      const r = (L.distance > 0 ? L.distance : 12) * 0.55; // tighter pool than the visual light — only lights sprites up close
      this.lights.push({ x: wp.x, z: wp.z, r, cr: L.color.r, cg: L.color.g, cb: L.color.b, i: L.intensity });
    });
    console.log(`[dayNight] collectLights → ${this.lights.length} point-lights`); // DEBUG
  }

  // DEBUG accessors (temporary) — to diagnose why lamps aren't lighting sprites.
  get debugNight(): number { return this.night; }
  get debugLightCount(): number { return this.lights.length; }
  nearestLampDist(x: number, z: number): number {
    let m = Infinity;
    for (const L of this.lights) { const d = Math.hypot(x - L.x, z - L.z); if (d - L.r < m) m = d - L.r; }
    return m; // <0 means the point is INSIDE a lamp's radius
  }

  /** Effective sprite colour at world (x,z): the day/night tint + additive contributions from nearby
   *  lamps (only at night). Writes into `out`. Cheap distance check; lamps far away are skipped. */
  lightAt(x: number, z: number, out: THREE.Color): void {
    out.copy(this.tint);
    if (this.night < 0.02 || this.lights.length === 0) return;
    for (const L of this.lights) {
      const dx = x - L.x, dz = z - L.z, d2 = dx * dx + dz * dz, r2 = L.r * L.r;
      if (d2 >= r2) continue;
      // Wide LINEAR falloff (reaches across the whole lamp radius) + a strong scale so standing under a
      // lamp clearly lights the figure back up toward daylight, not a faint tint. Night-gated.
      const f = 1 - Math.sqrt(d2) / L.r;
      const a = f * Math.min(1.6, L.i * 0.16) * this.night;
      out.r = Math.min(1.35, out.r + L.cr * a);
      out.g = Math.min(1.35, out.g + L.cg * a);
      out.b = Math.min(1.35, out.b + L.cb * a);
    }
  }

  /** Outdoor zone? show the sky rig + global lights + apply the day/night tint. Indoor → hide them all
   *  (the interior keeps its own lighting) + tint snaps to white. */
  setEnabled(on: boolean): void {
    if (on === this.enabled) return;
    this.enabled = on;
    if (this.skyGroup) this.skyGroup.visible = on;
    for (const l of [this.key, this.fill, this.hemi, this.ambient]) if (l) l.visible = on;
    this.setTime(this.time); // recompute the tint (white when disabled) + reapply
  }

  /** Temporarily show the sky rig + global day/night lights for an OFF-SCREEN render of an outdoor
   *  zone while the player is indoors (window views peeking outside). Visibility only — no tint
   *  recompute (cheap enough to run every view render). Returns the restore fn. */
  revealForSnapshot(): () => void {
    if (this.enabled) return () => { /* already outdoors — nothing hidden */ };
    const hidden = [this.skyGroup, this.key, this.fill, this.hemi, this.ambient]
      .filter((o): o is NonNullable<typeof o> => !!o && !o.visible);
    for (const o of hidden) o.visible = true;
    return () => { for (const o of hidden) o.visible = false; };
  }

  /** Sync the day/night clock to the server's wall clock (called on join). Time is then derived from
   *  the shared `timeOfDayAt(epoch)` so every client + every relogin shows the correct, matching time. */
  syncClock(serverEpochMs: number): void {
    this.clockOffsetMs = serverEpochMs - Date.now();
    this.synced = true;
    this.setTime(timeOfDayAt(Date.now() + this.clockOffsetMs));
  }

  /** Debug-panel: scrub to a fixed time (leaves the server sync — FREE mode). */
  scrub(t: number): void { this.synced = false; this.setTime(t); }
  /** Debug-panel: set the free-run speed (also leaves server sync). */
  setDaySpeed(s: number): void { this.synced = false; this.autoSpeed = s; }

  /** Advance the clock, drift the clouds, and refresh everything. */
  tick(dt: number): void {
    for (const c of this.clouds) { c.az = (c.az + c.speed * dt) % (Math.PI * 2); this.placeCloud(c); }
    // The tint changes over minutes, not frames — recompute ~5×/s. setTime walks every tinted sprite
    // in the world, so calling it 60×/s (the old behaviour) was the dominant CPU cost even in town.
    this.tickAccum += dt;
    if (this.tickAccum < 0.2) return;
    const elapsed = this.tickAccum;
    this.tickAccum = 0;
    if (this.synced) this.setTime(timeOfDayAt(Date.now() + this.clockOffsetMs));
    else if (this.autoSpeed > 0) this.setTime(this.time + elapsed * this.autoSpeed);
  }

  /** Interpolate the two phases bracketing hour `h` (wraps midnight). */
  private phaseAt(h: number): Phase {
    const n = PHASES.length;
    let i = 0;
    for (; i < n; i++) if (PHASES[i].h > h) break;
    const a = PHASES[(i - 1 + n) % n], b = PHASES[i % n];
    let span = b.h - a.h; if (span <= 0) span += 24;
    let d = h - a.h; if (d < 0) d += 24;
    const t = span > 0 ? Math.min(1, Math.max(0, d / span)) : 0;
    const C = (x: number, y: number): number => new THREE.Color(x).lerp(new THREE.Color(y), t).getHex();
    return {
      h, zenith: C(a.zenith, b.zenith), horizon: C(a.horizon, b.horizon),
      light: C(a.light, b.light), lightI: lerp(a.lightI, b.lightI, t),
      hemiSky: C(a.hemiSky, b.hemiSky), hemiGround: C(a.hemiGround, b.hemiGround), hemiI: lerp(a.hemiI, b.hemiI, t),
      ambient: C(a.ambient, b.ambient), ambientI: lerp(a.ambientI, b.ambientI, t),
      tint: C(a.tint, b.tint),
    };
  }

  setTime(t: number): void {
    this.time = ((t % 24) + 24) % 24;
    const p = this.phaseAt(this.time);
    const zen = new THREE.Color(p.zenith), hor = new THREE.Color(p.horizon);
    this.horizon.copy(hor);

    // Night factor (0 day → 1 deep night) from the sun elevation — gates the lamp lighting below.
    const sunY = Math.sin(((this.time - 6) / 12) * Math.PI); // >0 day, <0 night (matches the sun arc)
    this.night = this.enabled ? Math.min(1, Math.max(0, -sunY * 2 + 0.45)) : 0;

    // Daylight tint on all unlit sprites (trees, dropped items, players, NPCs, mobs) so they read as
    // day/night like the lit ground; positioned sprites also pick up nearby lamps at night (lightAt).
    // White while disabled (city zones keep their own look).
    this.tint.setHex(this.enabled ? p.tint : 0xffffff);
    // Re-tint every sprite ONLY when the colour actually moved. Indoors the tint snaps to white once
    // (then this loop is skipped forever); outdoors it re-applies as the daylight/lamp mix shifts.
    // Newly-registered sprites are tinted immediately in registerMat, so skipping here is safe.
    const tintHex = this.tint.getHex();
    if (tintHex !== this.lastTintHex || Math.abs(this.night - this.lastTintNight) > 0.005) {
      this.lastTintHex = tintHex;
      this.lastTintNight = this.night;
      for (const [mat, pos] of this.tintMats) {
        if (pos) this.lightAt(pos.x, pos.z, mat.color); else mat.color.copy(this.tint);
      }
    }

    // Repaint the gradient dome (throttled — small time steps reuse the last paint).
    if (this.skyCanvas && this.skyTex && Math.abs(this.time - this.lastGradH) > 0.04) {
      this.lastGradH = this.time;
      const ctx = this.skyCanvas.getContext("2d")!, H = this.skyCanvas.height;
      // Canvas top (y=0) = zenith (UV v=1, +Y pole); middle = horizon; bottom = below ground (unseen).
      const grad = ctx.createLinearGradient(0, 0, 0, H);
      grad.addColorStop(0, `#${zen.getHexString()}`);
      grad.addColorStop(0.46, `#${hor.getHexString()}`);
      grad.addColorStop(0.5, `#${hor.getHexString()}`);
      grad.addColorStop(1, `#${hor.clone().multiplyScalar(0.7).getHexString()}`);
      ctx.fillStyle = grad; ctx.fillRect(0, 0, this.skyCanvas.width, H);
      this.skyTex.needsUpdate = true;
    }

    // Sun arc: up at noon, below the horizon at night. Azimuth east(6h)→west(18h).
    const ang = ((this.time - 6) / 12) * Math.PI; // 0 at 6h, π at 18h
    const sunDir = new THREE.Vector3(Math.cos(ang), Math.sin(ang), 0.22).normalize();
    const moonDir = sunDir.clone().multiplyScalar(-1);
    const R = this.radius * 0.96;
    if (this.sun) {
      this.sun.position.set(sunDir.x * R, sunDir.y * R, sunDir.z * R); // local to the player-following group
      this.sun.visible = sunDir.y > -0.12;
      (this.sun.material as THREE.SpriteMaterial).color.copy(new THREE.Color(p.light));
      (this.sun.material as THREE.SpriteMaterial).opacity = Math.min(1, Math.max(0, sunDir.y * 2 + 0.5));
    }
    if (this.moon) {
      this.moon.position.set(moonDir.x * R, moonDir.y * R, moonDir.z * R);
      this.moon.visible = moonDir.y > -0.05;
    }
    if (this.stars) {
      // fade stars in as the sun sinks well below the horizon
      (this.stars.material as THREE.PointsMaterial).opacity = Math.min(1, Math.max(0, -sunDir.y * 2.2));
    }
    // Clouds: visible while the sun is up, tinted by the sun colour (warm at dawn/dusk, white midday).
    const dayF = Math.min(1, Math.max(0, sunDir.y * 3 + 0.15));
    const cloudCol = new THREE.Color(p.light);
    for (const c of this.clouds) {
      const m = c.sp.material as THREE.SpriteMaterial;
      m.opacity = this.enabled ? c.base * dayF : 0;
      m.color.copy(cloudCol);
    }

    // Lights follow the sun (day) or moon (night).
    const lit = sunDir.y > 0 ? sunDir : moonDir;
    if (this.key) { this.key.color.setHex(p.light); this.key.intensity = p.lightI; this.key.position.copy(lit).multiplyScalar(60); }
    if (this.fill) { this.fill.intensity = p.lightI * 0.3; this.fill.position.set(-lit.x, Math.abs(lit.y) * 0.6 + 0.3, -lit.z).multiplyScalar(50); }
    if (this.hemi) { this.hemi.color.setHex(p.hemiSky); this.hemi.groundColor.setHex(p.hemiGround); this.hemi.intensity = p.hemiI; }
    if (this.ambient) { this.ambient.color.setHex(p.ambient); this.ambient.intensity = p.ambientI; }
  }
}

/** Shared instance — the Kapon scene registers its dome + lights; world3d advances it + the panel scrubs it. */
export const dayNight = new DayNight();
